/**
 * Per-registration-service start-list descriptors.
 *
 * Each descriptor owns everything that differs between providers: where the
 * entries live, how a raw record maps onto the fixed columns, and how a
 * config-authored custom question resolves to an answer. Consumers supply only
 * the event's `options.<key>` slice, so nothing here depends on React, the
 * router, or how the event was obtained.
 *
 * `options` is unvalidated JSONB, hence the optional chaining throughout and
 * the separate `isConfigured` check.
 */

const getCityState = (city, state) =>
    [city, state].filter(x => !!x).join(', ');

const bikereg = {
    key: 'bikereg',
    isConfigured: (options) => Boolean(options?.eventId),
    path: (options) => `bikereg/events/${options.eventId}/entries`,
    hasRecord: (record) => Boolean(record?.UserInfo),
    baseColumns: [
        {
            key: 'athlete',
            label: 'Athlete',
            valueFn: r => `${r?.UserInfo?.LastName}, ${r?.UserInfo?.FirstName}`,
            sortFns: [
                record => record?.UserInfo?.LastName?.toLowerCase(),
                record => record?.UserInfo?.FirstName?.toLowerCase()
            ]
        },
        {
            key: 'team',
            label: 'Team',
            valueFn: r => r?.UserInfo?.Team || '',
            sortFns: [
                record => (record?.UserInfo?.Team || '').toLowerCase()
            ]
        },
        {
            key: 'class',
            label: 'Class',
            valueFn: r => r?.CategoryName || '',
            sortFns: [
                record => (record?.CategoryName || '').toLowerCase()
            ]
        }
    ],
    resolveQuestion: (record, cq) =>
        record?.CustomQuestions?.find(q =>
            q.QuestionAbbreviation === cq.label || q.QuestionID === cq.id
        )?.Answer,
    defaultSort: [
        r => r?.UserInfo?.LastName?.toLowerCase(),
        r => r?.UserInfo?.FirstName?.toLowerCase()
    ]
};

const runsignup = {
    key: 'runsignup',
    isConfigured: (options) => Boolean(options?.race_id && options?.event_id),
    path: (options) =>
        `runsignup/races/${options.race_id}/participants?event_id=${options.event_id}&include_questions=T`,
    hasRecord: (record) => Boolean(record?.user),
    baseColumns: [
        {
            key: 'athlete',
            label: 'Athlete',
            valueFn: r => `${r?.user?.first_name} ${r?.user?.last_name}`,
            sortFns: [
                record => record?.user?.last_name?.toLowerCase(),
                record => record?.user?.first_name?.toLowerCase()
            ]
        },
        {
            key: 'age',
            label: 'Age',
            valueFn: r => r?.age,
            sortFns: [
                record => record?.age
            ]
        },
        {
            key: 'town',
            label: 'Town',
            valueFn: r => getCityState(r?.user?.address?.city, r?.user?.address?.state),
            sortFns: [
                record => record?.user?.address?.city?.toLowerCase(),
                record => record?.user?.address?.state?.toLowerCase()
            ]
        }
    ],
    resolveQuestion: (record, cq) =>
        record?.question_responses?.find(qr => qr.question_text === cq.label)?.response,
    defaultSort: [
        r => r?.user?.last_name?.toLowerCase(),
        r => r?.user?.first_name?.toLowerCase()
    ]
};

const startListProviders = {
    bikereg,
    runsignup
};

// Returns undefined for an unknown key so callers can render their own
// "unavailable" state rather than throwing on a bad `data-reg-provider`.
const getStartListProvider = (key) =>
    startListProviders[String(key ?? '').toLowerCase()];

export {
    startListProviders,
    getStartListProvider
};
